import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { HedgehogLogo } from '../components/brand/HedgehogLogo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Lock, CheckCircle2, ArrowRight, AlertCircle, Check } from 'lucide-react';

export const ResetPasswordView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Missing password reset token in URL. Please request a new recovery link.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please re-enter.');
      return;
    }

    setIsLoading(true);
    try {
      await apiClient('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setIsSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Invalid or expired reset token. Please request a new password reset.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cf-bg flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="flex justify-center mb-3">
          <HedgehogLogo size={42} />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-cf-textPrimary">
          Choose a New Password
        </h2>
        <p className="mt-1 text-xs text-cf-textSecondary">
          Set a secure new password for your Ericius account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card padding="lg" className="shadow-xl">
          {!token && (
            <div className="mb-5 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-400 space-y-2">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle size={15} />
                <span>Invalid Reset Link</span>
              </div>
              <p className="text-xs text-amber-300/80">
                No reset token was found in the URL. Please request a new reset link.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block mt-2 text-xs text-cf-orange underline font-semibold"
              >
                Go to Forgot Password
              </Link>
            </div>
          )}

          {isSuccess ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
                <CheckCircle2 size={24} />
              </div>
              <h3 className="text-sm font-semibold text-cf-textPrimary">
                Password Reset Successfully
              </h3>
              <p className="text-xs text-cf-textSecondary leading-relaxed">
                Your password has been updated. You can now sign in using your new credentials.
              </p>
              <div className="pt-4">
                <Link to="/login" className="block w-full">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    fullWidth
                    icon={<ArrowRight size={14} />}
                  >
                    Sign In with New Password
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-3 rounded bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-xs text-rose-600 dark:text-rose-400 font-medium">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    label="New Password"
                    type="password"
                    required
                    size="sm"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    disabled={!token}
                    leftIcon={<Lock size={14} />}
                  />
                  <p className="mt-1 text-xs text-cf-textMuted flex items-center gap-1">
                    <Check
                      size={11}
                      className={password.length >= 8 ? 'text-emerald-600 dark:text-emerald-400' : 'text-cf-textMuted'}
                    />
                    <span>Must be at least 8 characters</span>
                  </p>
                </div>

                <div>
                  <Input
                    label="Confirm New Password"
                    type="password"
                    required
                    size="sm"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your new password"
                    disabled={!token}
                    leftIcon={<Lock size={14} />}
                    error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
                  />
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    fullWidth
                    loading={isLoading}
                    disabled={isLoading || !token || password.length < 8 || password !== confirmPassword}
                  >
                    Save New Password
                  </Button>
                </div>
              </form>

              <div className="mt-6 pt-5 border-t border-cf-border text-center">
                <Link
                  to="/login"
                  className="text-xs text-cf-textSecondary hover:text-cf-textPrimary transition-colors"
                >
                  Return to Sign In
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
